import { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api-client';

interface User {
  id: string;
  email: string;
  displayName: string | null;
  status: string;
  createdAt: string;
  adminRole: string | null;
  roleIsActive: boolean | null;
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteRole, setInviteRole] = useState<'client' | 'admin' | 'viewer'>('client');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);

      const data = await apiGet<User[]>('/api/admin/users');
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  }

  async function handleInviteUser() {
    try {
      if (!inviteEmail || !invitePassword) {
        setMessage({ type: 'error', text: 'Please fill in all fields' });
        return;
      }

      setMessage(null);

      await apiPost('/api/admin/users', {
        email: inviteEmail,
        password: invitePassword,
        role: inviteRole,
      });

      setMessage({ type: 'success', text: `User ${inviteEmail} created successfully!` });
      setShowInviteForm(false);
      setInviteEmail('');
      setInvitePassword('');
      setInviteRole('client');
      await loadUsers();
    } catch (err: unknown) {
      console.error('Error creating user:', err);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to create user' });
    }
  }

  async function handleToggleRole(userId: string, currentRole: string | null) {
    try {
      if (!currentRole) {
        await apiPost('/api/admin/roles', { userId, role: 'admin' });
        setMessage({ type: 'success', text: 'Admin role granted!' });
      } else {
        await apiDelete(`/api/admin/roles/${userId}`);
        setMessage({ type: 'success', text: 'Admin role revoked!' });
      }

      await loadUsers();
    } catch (err: unknown) {
      console.error('Error toggling role:', err);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update role' });
    }
  }

  async function handleApprove(userId: string) {
    try {
      await apiPatch(`/api/admin/users/${userId}/approve`, {});
      setMessage({ type: 'success', text: 'User approved!' });
      await loadUsers();
    } catch (err: unknown) {
      console.error('Error approving user:', err);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to approve user' });
    }
  }

  async function handleSuspend(userId: string) {
    try {
      await apiPatch(`/api/admin/users/${userId}/suspend`, {});
      setMessage({ type: 'success', text: 'User suspended.' });
      await loadUsers();
    } catch (err: unknown) {
      console.error('Error suspending user:', err);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to suspend user' });
    }
  }

  async function handleReactivate(userId: string) {
    try {
      await apiPatch(`/api/admin/users/${userId}/reactivate`, {});
      setMessage({ type: 'success', text: 'User reactivated!' });
      await loadUsers();
    } catch (err: unknown) {
      console.error('Error reactivating user:', err);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to reactivate user' });
    }
  }

  const getRoleBadge = (role: string | null, isActive: boolean | null) => {
    if (!role || !isActive) {
      return <span className="px-2 py-1 text-xs font-medium bg-slate-500/20 text-slate-400 rounded">Client</span>;
    }

    const badges = {
      super_admin: { color: 'bg-purple-500/20 text-purple-400', label: 'Super Admin' },
      admin: { color: 'bg-amber-500/20 text-amber-400', label: 'Admin' },
      viewer: { color: 'bg-blue-500/20 text-blue-400', label: 'Viewer' },
    };

    const badge = badges[role as keyof typeof badges] || badges.admin;
    return <span className={`px-2 py-1 text-xs font-medium rounded ${badge.color}`}>{badge.label}</span>;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Pending' },
      active: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Active' },
      suspended: { color: 'bg-red-500/20 text-red-400', label: 'Suspended' },
    };

    const badge = badges[status] ?? { color: 'bg-yellow-500/20 text-yellow-400', label: 'Pending' };
    return <span className={`px-2 py-1 text-xs font-medium rounded ${badge.color}`}>{badge.label}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">User Management</h2>
          <p className="text-slate-400 text-sm">Manage client users and admin roles</p>
        </div>

        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="px-4 py-2 bg-royal-gradient text-white font-medium rounded-lg
                   hover:opacity-90 transition-opacity shadow-glow flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          Add User
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Invite User Form */}
      {showInviteForm && (
        <div className="glass-strong p-6 rounded-xl border-2 border-amber-500/30">
          <h3 className="text-lg font-semibold text-white mb-4">Create New User</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Temporary Password</label>
              <input
                type="password"
                value={invitePassword}
                onChange={(e) => setInvitePassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                         placeholder:text-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1">User should change this on first login</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
                className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                         focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
              >
                <option value="client">Client (regular user)</option>
                <option value="viewer">Viewer (read-only admin)</option>
                <option value="admin">Admin (can manage settings)</option>
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleInviteUser}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg
                         transition-colors"
              >
                Create User
              </button>
              <button
                onClick={() => {
                  setShowInviteForm(false);
                  setInviteEmail('');
                  setInvitePassword('');
                }}
                className="px-4 py-2 bg-charcoal-700 hover:bg-charcoal-600 text-white font-medium rounded-lg
                         transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="glass-strong rounded-xl overflow-hidden">
        <div className="p-4 bg-charcoal-800/50 border-b border-charcoal-700">
          <h3 className="font-semibold text-white">All Users ({users.length})</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-slate-400">No users yet</p>
            <p className="text-sm text-slate-500 mt-1">Create your first user to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-charcoal-700">
            {users.map((user) => (
              <div key={user.id} className="p-4 hover:bg-charcoal-800/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="font-medium text-white">{user.email}</h4>
                      {getRoleBadge(user.adminRole, user.roleIsActive)}
                      {getStatusBadge(user.status)}
                    </div>
                    {user.displayName && (
                      <p className="text-sm text-slate-400">{user.displayName}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      Joined: {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    {user.status === 'pending' && (
                      <button
                        onClick={() => handleApprove(user.id)}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                                 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"
                      >
                        Approve
                      </button>
                    )}
                    {user.status === 'active' && (
                      <button
                        onClick={() => handleSuspend(user.id)}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                                 bg-red-500/20 hover:bg-red-500/30 text-red-400"
                      >
                        Suspend
                      </button>
                    )}
                    {user.status === 'suspended' && (
                      <button
                        onClick={() => handleReactivate(user.id)}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                                 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"
                      >
                        Reactivate
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleRole(user.id, user.adminRole)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        user.adminRole && user.roleIsActive
                          ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                          : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400'
                      }`}
                    >
                      {user.adminRole && user.roleIsActive ? 'Revoke Admin' : 'Make Admin'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="glass p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-400 mb-1">User Roles</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>* <strong>Client:</strong> Regular user (chat, habits tracking)</li>
              <li>* <strong>Viewer:</strong> Can view admin settings (read-only)</li>
              <li>* <strong>Admin:</strong> Can modify theme, branding, AI config, documents</li>
              <li>* <strong>Super Admin:</strong> Full access (API keys, user management)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
